// Service for workout-related API calls
import { fetchWithAuth } from '../../utils/api'
import { API_URL } from '../../utils';

export class WorkoutApiService {
  static async deleteWorkoutSet(setId: number) {
    return fetchWithAuth(`${API_URL}/workouts/${setId}`, { method: 'DELETE' });
  }

  static async saveAmrapWorkout(dateKey: string, amrap_time_minutes: string, comment: string, amrap_details: Array<{ exercise_id: number, reps: string }>) {
    return fetchWithAuth(`${API_URL}/workouts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        date: dateKey,
        workout_type: 'amrap',
        amrap_time_minutes: parseInt(amrap_time_minutes, 10),
        comment: comment.trim() || null,
        amrap_details: amrap_details.map((detail: any) => ({
          exercise_id: detail.exercise_id,
          reps: parseInt(detail.reps, 10)
        }))
      }),
    });
  }

  static async addTraditionalWorkout(dateKey: string, exerciseId: number, comment: string, details: Array<any>) {
    return fetchWithAuth(`${API_URL}/workouts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        date: dateKey,
        exerciseId: exerciseId,
        comment: comment.trim() || null,
        details: details.map((detail: any) => ({
          series: parseInt(detail.series, 10),
          reps: parseInt(detail.reps, 10),
          weight: parseFloat(detail.weight),
          rest_time_seconds: detail.rest_time_seconds && detail.rest_time_seconds.trim() !== '' ? parseInt(detail.rest_time_seconds, 10) : null,
          rir: detail.rir && detail.rir.trim() !== '' ? parseInt(detail.rir, 10) : null
        }))
      }),
    });
  }

  static async getWorkoutsForDate(dateKey: string) {
    const response = await fetchWithAuth(`${API_URL}/workouts?date=${dateKey}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch workouts: ${response.status}`);
    }
    return response.json();
  }

  static async getWorkoutCount() {
    const response = await fetchWithAuth(`${API_URL}/workouts/count`);
    if (!response.ok) {
      throw new Error(`Failed to fetch workout count: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Update a traditional workout set
   * @param setId - The workout set ID
   * @param data - Updated workout data
   * @returns Response from the API
   */
  static async updateWorkoutSet(
    setId: number,
    data: {
      exerciseId?: number | null;
      comment?: string | null;
      details: Array<{
        id?: number;
        series: number;
        reps: number;
        weight: number;
        rest_time_seconds?: number | null;
        rir?: number | null;
      }>;
    }
  ): Promise<Response> {
    return fetchWithAuth(`${API_URL}/workouts/${setId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /**
   * Update an AMRAP workout
   * @param amrapTimeSeconds - The original AMRAP time in seconds (used as identifier)
   * @param data - Updated AMRAP workout data
   * @returns Response from the API
   */
  static async updateAmrapWorkout(
    amrapTimeSeconds: number,
    data: {
      time_minutes: number;
      comment: string | null;
      amrap_details: Array<{
        exercise_id: number;
        reps: number;
      }>;
    }
  ): Promise<Response> {
    return fetchWithAuth(`${API_URL}/workouts/amrap/${amrapTimeSeconds}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a specific workout detail (set)
   * @param detailId - The workout detail ID
   * @returns Response from the API
   */
  static async deleteWorkoutDetail(detailId: number): Promise<Response> {
    return fetchWithAuth(`${API_URL}/workouts/detail/${detailId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Fetch all dates with workouts for a specific month
   * @param year - Year (e.g., 2026)
   * @param month - Month number (1-12)
   * @returns Array of date strings in YYYY-MM-DD format
   */
  static async getWorkoutDates(year: number, month: number): Promise<string[]> {
    const monthStr = String(month).padStart(2, '0');
    const response = await fetchWithAuth(`${API_URL}/workouts/dates?month=${year}-${monthStr}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch workout dates: ${response.status}`);
    }
    const dates = await response.json();
    return Array.isArray(dates) ? dates : [];
  }
}
 